/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_POLICY_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { usePolicyId } from "../provider";
import { toHeaderCase } from "js-convert-case";
import config from "../config/featuresSection.config.json";
import moment from "moment";
import jwt_decode from "jwt-decode";

import { AppNavigatorService } from "../../appNavigatorService";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const lookup = require("country-code-lookup");

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const { policyNumber, userVid } = usePolicyId();

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_POLICY_DETAIL,
          variables: {
            input: {
              policyId: policyNumber,
            },
            input2:{
              vuid:userVid
            }
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getPolicyDetail) {
          console.log(`policy details`, res?.data?.getPolicyDetail);
          let _result = res?.data?.getPolicyDetail;
          return {
            title: _result?.policyNumber,
            subTitle: _result?.policyNumber,
            listItems: this.getListItems(_result),
            beneficiaryDetails: this.getBeneficiary(_result?.benificiaryDetail),
            policyDocument: `data:application/pdf;base64,${_result?.pdf}`,
            accordionItems:this.getAccordionItems(_result),
            policyDetails: this.getPolicyDetails(_result),
            carouselItems: this.getCarouselItems(_result),
            useOfProceedDetails: this.getUseOfProceeds(_result),
            // buttons: await this.getButtons(),
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  private async getButtons() {
    let whoami = await getWhoamiSrv()?.whoAmI();
    if (whoami?.whoami === "INSURANCE_PROVIDER") {
      return config?.buttons;
    } else{
      return [];
    }
  }

  private getAccordionItems(res:any, txList:any){
    let items:any = [];
    let _policyHolder = this.getPolicyDetails(res);
    if (res?.description) {
      items.push({
          type: "text",
          title: "Description",
          "data": [
              res?.description
          ]
      })
    }
    if (res?.purchasedBy?.bankDetail) {
      let _temp = this.getBankDetails(res?.purchasedBy?.bankDetail);
      items.push({
        type: "multi-col-list",
        numberOfColumns: "3",
        title: _temp?.title,
        data: [..._temp?.listItems]
      });
    }
    if(_policyHolder){
      items?.push({
        type:"multi-col-list",
        numberOfLines:"3",
        title:_policyHolder?.title,
        data:[..._policyHolder?.listItems]
      })
    }

    if(res?.purchasedBy){
      let _holder = this.getPolicyHolder(res?.purchasedBy);
      if(_holder){
        items?.push({
          type:"multi-col-list",
          numberOfColumns:"3",
          title:_holder?.title,
          data:[..._holder?.listItems]
        })
      }
    }
    
    if(res?.benificiaryDetail){
      let _temp = this.getBeneficiary(res?.benificiaryDetail);
      items.push({
        type:"multi-col-list",
        numberOfLines:"3",
        title:_temp?.title,
        data:[..._temp?.listItems]
      });
    }

    let _useOfProceeds = this.getUseOfProceeds(res);
    if(_useOfProceeds){
      items.push({
        type:"check-list",
        title:_useOfProceeds?.title,
        data:[..._useOfProceeds?.listItems]
      })
    }

    return items;
  }

  private getPolicyHolder(data:any) {
    let _temp = [];
    _temp = config?.policyHolder?.map(ele=>{
      if(ele?.key === "address"){
        return({
          ...ele,
          subTitle: `${data[ele?.key]?.addressLine1}, ${data[ele?.key]?.city}, ${data[ele?.key]?.country && data[ele?.key]?.country.length ===2 ? lookup.byIso(`${data[ele?.key]?.country}`)?.country: "-"}`
        })
      }
      else if(ele?.key === "firstName" || ele?.key === "lastName"){
        return({
          ...ele,
          subTitle: toHeaderCase(data[ele?.key] ?? "-")
        })
      }
      else return({
        ...ele,
        subTitle:data[ele?.key]
      })
    });
    return {
      title: config?.purchaser?.title,
      listItems: _temp,
    };
  }

  private getCarouselItems(data: any) {
    let _temp = [];
    let _kyc = [];
    if (data?.pdf) {
      _temp.push({
        type: "pdf",
        poster: `data:application/pdf;base64,${data?.pdf}`,
        src: `data:application/pdf;base64,${data?.pdf}`,
        title: "Policy Contract",
      });
    }
    if (Array.isArray(data?.purchasedBy?.kyc)) {
      _kyc = data?.purchasedBy?.kyc.map((element: any) => {
        return {
          ...element,
          type: "img",
          src: element?.doc,
          title: element?.name,
          poster: element?.poster,
        };
      });
    }

    return [..._temp, ..._kyc];
  }

  private getBeneficiary(res: any) {
    let _beneficiary = {
      firstName: res?.firstName,
      lastName: res?.lastName,
      email: res?.email,
      phoneNumber: res?.mobileNo,
      streetAddress: res?.address?.addressLine1,
      city: res?.address?.city,
      country: res?.address?.country
        ? lookup.byIso(`${res?.address?.country}`)?.country
        : "-",
    };
    let _temp = [];
    for (const [key, value] of Object.entries(_beneficiary)) {
      _temp.push({
        title: toHeaderCase(key),
        subTitle: value,
      });
    }
    return {
      title: config?.beneficiaryDetails?.title,
      listItems: _temp,
    };
  }

  private getBankDetails(res: any) {
    let _temp = [];
    for (const [key, value] of Object.entries(res)) {
      _temp.push({
        title: toHeaderCase(key),
        subTitle: value,
      });
    }

    return {
      title: config?.bankDetails?.title,
      listItems: _temp,
    };
  }

  private getPolicyDetails(res: any) {
    let _temp = [
      {
        title: "Policy Number",
        subTitle: res?.policyNumber,
      },
      {
        title: "Policy Option",
        subTitle: `Policy Option ${res?.productDefination}`,
      },
      {
        title: "Coverage Amount",
        subTitle: res?.coverage,
      },
      {
        title: "Annual Premium",
        subTitle: res?.premium,
      },
      {
        title: "Purchase on",
        subTitle: moment(res?.purchasedAt).format("DD/MM/YYYY"),
      },
      {
        title: "Policy Type",
        subTitle: toHeaderCase(res?.type ?? "-"),
      },
      {
        title: "Insured Country",
        subTitle: res?.countryToBeInsured
          ? lookup.byIso(`${res?.countryToBeInsured}`)?.country
          : "-",
      },
    ];
    return {
      title: config?.policyDetails?.title,
      listItems: _temp,
    };
  }
  private getListItems(res: any) {
    let _temp = config?.listItems?.map((ele: any) => {
      if (ele?.key === "policyName") {
        return {
          ...ele,
          subTitle: `Policy Option ${res?.productDefination}`,
        };
      }
      if (ele?.key === "coverage") {
        return {
          ...ele,
          subTitle: res?.coverage,
        };
      }
      if (ele?.key === "premium") {
        return {
          ...ele,
          subTitle: res?.premium,
        };
      }
      if (ele?.key === "country") {
        return {
          ...ele,
          subTitle: res?.countryToBeInsured
            ? lookup.byIso(`${res?.countryToBeInsured}`)?.country
            : "-",
          flag: res?.countryToBeInsured,
        };
      }
    });
    return _temp;
  }

  private getUseOfProceeds(data: any) {
    let _temp: any = [];
    Array.isArray(data?.useOfProceeds) &&
      data?.useOfProceeds?.forEach((element: any) => {
        if (element.selected) {
          _temp.push(
            toHeaderCase(element?.value ?? ""))
        }
    });

    return {
      title: config?.useOfProceedsDetail?.title,
      listItems: _temp,
    };
  }

  private getTransactionDetail(items: any) {
    let _temp = [];
    _temp =
      Array.isArray(items?.data) &&
      items?.data?.map((ele) => {
        console.log(`date when:`, new Date(ele?.when));
        return {
          title: toHeaderCase(ele?.action),
          cardTitle: toHeaderCase(ele?.action),
          cardSubtitle: ele?.payload?.policyNumber,
          cardDetailedText: ele?.when
            ? moment(parseInt(ele?.when)).format("LLL")
            : null,
        };
      });
    if (_temp?.length > 0) {
      return {
        title: config?.timelineDetails?.title,
        listItems: _temp,
      };
    }
  }
}
